import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Lock, Shield, Sparkles, Info, CheckCircle2, XCircle, Heart } from 'lucide-react';
import Disclaimer from '../components/Disclaimer';
import AboutSelah from '../components/AboutSelah';
import RotatingScripture from '../components/RotatingScripture';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [confirmedAge, setConfirmedAge] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!agreedToTerms) {
      toast.error('Please agree to the Terms and Conditions and Privacy Policy');
      return;
    }

    if (!confirmedAge) {
      toast.error('Please confirm you are at least 13 years old.');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      await register(email, password, name);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex">
        {/* Left side - Brand */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 flex flex-col justify-center px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* App Title */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles size={40} className="text-[var(--sd-primary)]" />
                <h1 className="text-5xl font-brand text-[var(--sd-fg)]">
                  Selah Downloads™
                </h1>
              </div>
              <p className="text-xl text-[var(--sd-muted-fg)] italic font-scripture pl-14">
                Pause. Listen. Receive.
              </p>
            </div>

            <div className="mb-8">
              <h2 className="text-3xl font-semibold mb-4 text-[var(--sd-fg)]">
                Begin your spiritual journey
              </h2>
              <p className="text-lg text-[var(--sd-muted-fg)] leading-relaxed">
                Capture divine moments, receive personalized insights, and grow in faith with AI-powered spiritual guidance.
              </p>
            </div>

            {/* Bible Verse (rotates) */}
            <div className="mb-6">
              <RotatingScripture
                scriptureKey="register-hero"
                mood="guidance"
                variant="card"
                center={false}
                className="p-6"
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Shield size={20} className="text-[var(--sd-primary)] mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium">Privacy-First</p>
                  <p className="text-sm text-[var(--sd-muted-fg)]">
                    All your entries are encrypted on your device
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Lock size={20} className="text-[var(--sd-primary)] mt-1 flex-shrink-0" />
                <div>
                  <p className="font-medium">Secure Storage</p>
                  <p className="text-sm text-[var(--sd-muted-fg)]">
                    Your spiritual moments remain completely private
                  </p>
                </div>
              </div>
            </div>

            {/* About Selah — compact version */}
            <div className="mt-8 pt-8 border-t border-[rgba(184,120,132,0.25)]">
              <AboutSelah variant="compact" />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-[var(--sd-bg)]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="w-full max-w-md"
        >
          {/* Mobile Title */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles size={32} className="text-[var(--sd-primary)]" />
              <h1 className="text-3xl font-brand text-[var(--sd-fg)]">
                Selah Downloads™
              </h1>
            </div>
            <p className="text-sm text-[var(--sd-muted-fg)] italic font-scripture">
              Pause. Listen. Receive.
            </p>
          </div>

          <Card className="glass-card p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-semibold mb-2">Create your account</h2>
              <p className="text-sm text-[var(--sd-muted-fg)] mb-3">
                Start capturing your spiritual downloads today
              </p>
              <div className="flex items-center gap-2 text-sm bg-[var(--sd-primary)]/10 text-[var(--sd-primary)] px-3 py-2 rounded-lg border border-[var(--sd-primary)]/30 mb-3">
                <Sparkles size={16} />
                <span className="font-medium">7-day free trial • No credit card required</span>
              </div>
              <div className="flex items-start gap-2 text-xs text-[var(--sd-muted-fg)] bg-[rgba(245,245,245,0.05)] px-3 py-2 rounded-lg border border-[rgba(245,245,245,0.08)]">
                <Info size={14} className="flex-shrink-0 mt-0.5" />
                <p>
                  Trial automatically cancels after 7 days unless you subscribe. Your downloads are saved and accessible anytime if you choose to resubscribe later.
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-2">
                  Name
                </label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                  disabled={isLoading}
                  data-testid="register-name-input"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  disabled={isLoading}
                  data-testid="register-email-input"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-2">
                  Password
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                  data-testid="register-password-input"
                  className="w-full"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">
                  Confirm Password
                </label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                  data-testid="register-confirm-password-input"
                  className="w-full"
                />
              </div>

              {/* Age confirmation \u2014 store submission compliance (Apple/Google 13+) */}
              <div className="flex items-start space-x-3 pt-3">
                <Checkbox
                  id="age-gate"
                  checked={confirmedAge}
                  onCheckedChange={setConfirmedAge}
                  disabled={isLoading}
                  data-testid="register-age-checkbox"
                  className="mt-1"
                />
                <label htmlFor="age-gate" className="text-sm text-[var(--sd-muted-fg)] leading-relaxed cursor-pointer">
                  I confirm I am at least <span className="text-[var(--sd-fg)] font-medium">13 years old</span> (or the minimum digital-consent age in my country).
                </label>
              </div>

              {/* Terms and Privacy Consent */}
              <div className="flex items-start space-x-3 py-2">
                <Checkbox
                  id="terms"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  disabled={isLoading}
                  data-testid="register-terms-checkbox"
                  className="mt-1"
                />
                <label htmlFor="terms" className="text-sm text-[var(--sd-muted-fg)] leading-relaxed cursor-pointer">
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" className="text-[var(--sd-primary)] hover:underline">
                    Terms and Conditions
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" target="_blank" className="text-[var(--sd-primary)] hover:underline">
                    Privacy Policy
                  </Link>
                </label>
              </div>

              <Button
                type="submit"
                disabled={isLoading || !agreedToTerms || !confirmedAge}
                data-testid="register-submit-button"
                className="w-full bg-[var(--sd-primary)] hover:bg-[#D7A1A8] text-[var(--sd-primary-fg)] font-semibold disabled:opacity-50"
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-[var(--sd-muted-fg)]">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-[var(--sd-primary)] hover:underline font-medium"
                  data-testid="register-login-link"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </Card>

          {/* Mobile Bible Verse */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="lg:hidden mt-6"
          >
            <RotatingScripture
              scriptureKey="register-mobile"
              mood="guidance"
              variant="compact"
            />
          </motion.div>
        </motion.div>
      </div>
      </div>

      <Disclaimer />
    </div>
  );
};

export default Register;